import React from "react";
import {
  ArrowRight,
  Trash2,
  Share2,
  Pencil,
  Clock,
  Pin,
  MoreHorizontal,
} from "lucide-react";
import { Card } from "components/ui/card";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import ToolIcon from "./ToolIcon";

// CU-86cb3mq0m: the always-visible icon row is replaced by a "…" overflow
// menu, matching the Lovable TaskCard (Automate / Share / Edit / Pin / Delete).
const MENU_ACTIONS = [
  { Icon: Clock, label: "Automate", prop: "onSchedule" },
  { Icon: Share2, label: "Share", prop: "onShare" },
  { Icon: Pencil, label: "Edit", prop: "onEdit" },
  { Icon: Pin, label: "Pin", prop: "onPin" },
  { Icon: Trash2, label: "Delete", prop: "onDelete" },
];

// CU-86cb3me3q: mono display title whose leading verb flips to the task
// accent on hover, the rest lifting from muted to foreground.
const SplitTitle = ({ title }) => {
  const trimmed = (title || "").trim();
  const idx = trimmed.indexOf(" ");
  const verb = idx === -1 ? trimmed : trimmed.slice(0, idx);
  const rest = idx === -1 ? "" : trimmed.slice(idx);
  return (
    <>
      <span className="text-foreground transition-colors duration-150 group-hover:text-[hsl(var(--task-accent))]">
        {verb}
      </span>
      <span className="text-muted-foreground transition-colors duration-150 group-hover:text-foreground">
        {rest}
      </span>
    </>
  );
};

const TaskCard = ({
  title,
  description,
  metaLabel,
  tools = [],
  buttonLabel = "Run now",
  onClick,
  showActions = true,
  // When the Task already repeats, the strip reads "Edit schedule / RUNS <cadence>".
  // Otherwise it invites automation: "Automate it / SUGGESTED <cadence>".
  scheduleLabel,
  suggestedLabel,
  onDelete,
  onShare,
  onEdit,
  onSchedule,
  onPin,
  animationDelay,
}) => {
  const handlers = { onDelete, onShare, onEdit, onSchedule, onPin };

  return (
    <Card
      onClick={onClick}
      style={animationDelay != null ? { animationDelay: `${animationDelay}ms` } : undefined}
      className="task-card-animate group relative rounded-xl p-5 bg-card border border-border transition-all duration-200 hover:bg-[hsl(var(--task-accent)/0.04)] hover:border-[hsl(var(--task-accent)/0.45)] hover:shadow-[0_10px_30px_-12px_hsl(var(--task-accent)/0.25)] cursor-pointer h-full flex flex-col"
    >
      <h3 className="font-mono text-base font-semibold leading-snug mb-1.5 line-clamp-2">
        <SplitTitle title={title} />
      </h3>
      <div
        className="text-sm text-muted-foreground leading-relaxed mb-5 line-clamp-2 overflow-hidden"
        dangerouslySetInnerHTML={{ __html: description || "" }}
      />

      <div className="mt-auto flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span className="whitespace-nowrap">{metaLabel}</span>
          {tools.length > 0 && (
            <div className="flex items-center gap-1">
              {tools.map((tool, i) => (
                <ToolIcon key={i} type={tool} />
              ))}
            </div>
          )}
        </div>

        {/* CU-86cb3mk78: full-width Run now CTA that inverts to foreground on
            card hover, with the overflow menu beside it. */}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onClick?.(e);
            }}
            className="flex-1 gap-1.5 px-3 h-[36px] rounded-md border-border bg-background text-sm font-semibold text-foreground transition-colors group-hover:bg-foreground group-hover:text-background group-hover:border-foreground hover:bg-foreground hover:text-background hover:border-foreground relative z-10"
          >
            {buttonLabel}
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
          {showActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="More actions"
                  onClick={(e) => e.stopPropagation()}
                  className="h-7 w-7 shrink-0 rounded-md border border-border bg-background inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
                >
                  <MoreHorizontal className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {MENU_ACTIONS.map(({ Icon, label, prop }) => (
                  <DropdownMenuItem
                    key={label}
                    onSelect={(e) => {
                      e.preventDefault();
                      handlers[prop]?.(e);
                    }}
                  >
                    <Icon className="w-3.5 h-3.5 mr-2" />
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* CU-123yxuah4fu: the Lovable card closes on an automation strip.
            Scheduled Tasks show their cadence and a way back into it; the
            rest invite automation with a deterministic suggestion. */}
        {scheduleLabel ? (
          <div className="-mx-5 -mb-5 mt-1 px-5 py-3 border-t border-border bg-muted/40 rounded-b-xl flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(e);
              }}
              className="text-xs text-[hsl(var(--task-accent))] hover:underline font-semibold whitespace-nowrap"
            >
              Edit schedule
            </button>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground min-w-0">
              <span className="page-eyebrow text-[10px]">Runs</span>
              <span className="font-medium text-foreground/80 truncate">
                {scheduleLabel}
              </span>
            </div>
          </div>
        ) : suggestedLabel ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(e);
            }}
            aria-label="Automate this Task"
            className="-mx-5 -mb-5 mt-1 px-5 py-3 border-t border-border bg-muted/40 rounded-b-xl flex items-center justify-between gap-3 text-left transition-colors hover:bg-[hsl(var(--task-accent)/0.06)]"
          >
            <span className="text-xs text-[hsl(var(--task-accent))] font-semibold whitespace-nowrap group-hover:underline">
              Automate it
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground min-w-0">
              <span className="page-eyebrow text-[10px]">Suggested</span>
              <span className="font-medium text-foreground/80 truncate">
                {suggestedLabel}
              </span>
            </span>
          </button>
        ) : null}
      </div>
    </Card>
  );
};

export default TaskCard;
